import api from '@/services/apiService';

export interface WhatsappStatusResponse {
  status: 'pending' | 'active' | 'disconnected' | string;
  qr?: string | null; // base64 or url to QR image
  message?: string;
  error?: string;
}

export interface WhatsappMessageItem {
  id: string;
  from: string;
  to: string;
  body: string;
  status: 'sent' | 'error' | string;
  created_at: string;
  error_detail?: string | null;
}

export interface WhatsappMessagesResponse {
  data: WhatsappMessageItem[] | null;
  total?: number;
  error?: string;
}

export interface SessionMessagesPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SessionMessagesResponse {
  success: boolean;
  data: {
    messages: WhatsappMessageItem[];
    pagination?: SessionMessagesPagination;
  };
}

export interface WhatsappDataSessionResponse {
  session_id: string;
  arena_id: number;
  qr_code: string | null;
  device_name: string;
  phone_number: string | null;
  status: 'pending' | 'active' | 'disconnected' | string;
  last_seen: string | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsappSessionResponse {
  success: boolean;
  message: string;
  data: WhatsappDataSessionResponse;
}

export interface WhatsappCreateSessionResponse {
  success: boolean;
  message: string;
  data?: {
    qrCode?: string;
    sessionId?: string;
  };
}

// Get current whatsapp integration status (pending|active|disconnected)
export const getWhatsappStatus = async (): Promise<WhatsappStatusResponse> => {
  try {
    const response = await api.get('/whatsapp/status');

    if (response.status !== 200) {
      return {
        status: 'disconnected',
        error: response.data?.message ?? `Request failed: ${response.status}`,
      } as WhatsappStatusResponse;
    }

    return response.data as WhatsappStatusResponse;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Unknown error';
    return { status: 'disconnected', error: msg } as WhatsappStatusResponse;
  }
};

export const getSession = async (): Promise<WhatsappSessionResponse> => {
  try {
    const response = await api.get('/whatsapp/session');

    if (response.status !== 200) {
      return {
        success: false,
        message: response.data?.message ?? `Request failed: ${response.status}`,
        data: {} as WhatsappDataSessionResponse,
      } as WhatsappSessionResponse;
    }

    return response.data as WhatsappSessionResponse;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Unknown error';
    return {
      success: false,
      message: msg,
      data: {} as WhatsappDataSessionResponse,
    } as WhatsappSessionResponse;
  }
};

// Request QR code (this can be same as getWhatsappStatus or separate depending on API)
export const getWhatsappQRCode = async (): Promise<WhatsappCreateSessionResponse> => {
  try {
    const response = await api.post('/whatsapp/session', {});

    if (response.status !== 200) {
      return {
        success: false,
        message: response.data?.message ?? `Request failed: ${response.status}`,
        data: {} as WhatsappDataSessionResponse,
      } as WhatsappCreateSessionResponse;
    }

    return response.data as WhatsappCreateSessionResponse;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Unknown error';
    return {
      success: false,
      message: msg,
      data: {} as WhatsappDataSessionResponse,
    } as WhatsappCreateSessionResponse;
  }
};

// Refresh QR / request new QR if disconnected
export const refreshWhatsappQRCode = async (): Promise<WhatsappStatusResponse> => {
  try {
    const response = await api.post('/whatsapp/qr/refresh', {});

    if (response.status !== 200 && response.status !== 201) {
      return {
        status: 'disconnected',
        error: response.data?.message ?? `Request failed: ${response.status}`,
      } as WhatsappStatusResponse;
    }

    return response.data as WhatsappStatusResponse;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Unknown error';
    return { status: 'disconnected', error: msg } as WhatsappStatusResponse;
  }
};

// Get messages with optional filters (status, pagination)
export const getWhatsappMessages = async (params?: {
  status?: string;
  page?: number;
  per_page?: number;
}): Promise<WhatsappMessagesResponse> => {
  try {
    const response = await api.get('/whatsapp/messages', { params });

    if (response.status !== 200) {
      return {
        data: null,
        error: response.data?.message ?? `Request failed: ${response.status}`,
      } as WhatsappMessagesResponse;
    }

    return response.data as WhatsappMessagesResponse;
  } catch (e) {
    const err = e as any;
    const msg =
      err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Unknown error';
    return { data: null, error: msg } as WhatsappMessagesResponse;
  }
};

// Get all messages for a given session with pagination (page, limit)
export const getSessionMessages = async (
  page: number = 1,
  limit: number = 20
): Promise<SessionMessagesResponse> => {
  try {
    const response = await api.get(`/whatsapp/messages`, {
      params: { page, limit },
    });

    if (response.status !== 200) {
      return {
        success: false,
        data: { messages: [] },
      } as SessionMessagesResponse;
    }

    return response.data as SessionMessagesResponse;
  } catch (_e) {
    return {
      success: false,
      data: { messages: [] },
    } as SessionMessagesResponse;
  }
};

export default {
  getWhatsappStatus,
  getWhatsappQRCode,
  refreshWhatsappQRCode,
  getSession,
  getWhatsappMessages,
  getSessionMessages,
};
